import { useEffect, useRef, useState } from "react";
import { ImagePlus, Plus, Save, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, DOMAINES, domaineLabel, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export const imageSrc = (url) => (!url ? null : url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

export const ImageField = ({ url, onChange, testid, label = "photo" }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const envoyer = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/trees/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.image_url);
      toast.success(url ? "Photo remplacée" : "Photo ajoutée");
    } catch (e) {
      toast.error(errMsg(e, "Envoi de la photo impossible"));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      {url ? (
        <img src={imageSrc(url)} alt="" className="h-14 w-20 object-cover rounded-lg border border-slate-200" />
      ) : (
        <div className="h-14 w-20 rounded-lg border border-dashed border-slate-300 grid place-items-center text-slate-300">
          <ImagePlus size={18} />
        </div>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid={`${testid}-input`} onChange={(e) => envoyer(e.target.files?.[0])} />
      <Button variant="outline" size="sm" disabled={busy} data-testid={`${testid}-btn`} onClick={() => inputRef.current?.click()}>
        {busy ? "Envoi…" : url ? `Remplacer la ${label}` : `Ajouter une ${label}`}
      </Button>
      {url && (
        <button data-testid={`${testid}-remove`} onClick={() => onChange(null)} className="text-slate-300 hover:text-red-500">
          <Trash2 size={15} />
        </button>
      )}
    </div>
  );
};

const NodeImage = ({ node, onChange }) => (
  <div className="pt-1">
    <ImageField testid={`node-image-${node.id}`} url={node.image_url} onChange={onChange} />
  </div>
);

const emptyTree = (domaine) => ({
  domaine,
  titre: `${domaineLabel(domaine)} — nouvelle fiche`,
  statut: "brouillon",
  root_node_id: "q1",
  nodes: [{ id: "q1", question: "Quel est le type de problème ?", answers: [{ label: "Physique", next_node_id: null, solution: null }] }],
});

export default function AdminTrees() {
  const [trees, setTrees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/trees");
    setTrees(data);
    return data;
  };
  useEffect(() => {
    load();
  }, []);

  const create = async (domaine) => {
    const { data } = await api.post("/admin/trees", emptyTree(domaine));
    await load();
    setSelected(data);
    toast.success("Fiche créée en brouillon");
  };

  const save = async (statut) => {
    const payload = { domaine: selected.domaine, titre: selected.titre, statut: statut || selected.statut, root_node_id: selected.root_node_id, nodes: selected.nodes };
    const { data } = await api.put(`/admin/trees/${selected.id}`, payload);
    setSelected(data);
    await load();
    toast.success(statut === "publie" ? "Fiche publiée (version en prod)" : "Fiche enregistrée");
  };

  const remove = async (tree) => {
    await api.delete(`/admin/trees/${tree.id}`);
    if (selected?.id === tree.id) setSelected(null);
    load();
  };

  const publierEnrichis = async () => {
    if (!window.confirm("Publier les arbres enrichis (savoir terrain) sur les 6 domaines ? Les versions actuelles sont archivées, rien n'est supprimé.")) return;
    setPublishing(true);
    try {
      const { data } = await api.post("/admin/trees/publier-enrichis");
      toast.success(`${data.publies.length} arbres publiés : ${data.publies.map((p) => `${domaineLabel(p.domaine)} v${p.version}`).join(", ")}`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Publication impossible"));
    } finally {
      setPublishing(false);
    }
  };

  const updateNode = (idx, patch) => {
    const nodes = selected.nodes.map((n, i) => (i === idx ? { ...n, ...patch } : n));
    setSelected({ ...selected, nodes });
  };

  const updateAnswer = (nodeIdx, ansIdx, patch) => {
    const nodes = selected.nodes.map((n, i) =>
      i === nodeIdx ? { ...n, answers: n.answers.map((a, j) => (j === ansIdx ? { ...a, ...patch } : a)) } : n
    );
    setSelected({ ...selected, nodes });
  };

  return (
    <div className="space-y-6" data-testid="admin-trees">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="bk-label mb-2">Éditeur no-code</div>
          <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900">Fiches types (arbres QCM)</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {DOMAINES.map((d) => (
            <Button key={d.key} data-testid={`tree-create-${d.key}`} variant="outline" size="sm" onClick={() => create(d.key)}>
              <Plus size={14} className="mr-1" /> {d.label}
            </Button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-[#005CFF]/30 bg-blue-50/40 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="tree-enrichis-panel">
        <div className="max-w-2xl">
          <div className="bk-label text-[#0047CC]">Arbres enrichis par le savoir terrain</div>
          <p className="text-sm text-slate-600 mt-1">
            Publie en une fois les parcours issus des pannes réelles (codes Daikin, PoE et NVR UNV, adoption UniFi,
            LED Loxone, différentiel et contacteur jour/nuit, codes onduleur). Les versions en place sont archivées :
            vous pouvez revenir en arrière depuis la liste.
          </p>
        </div>
        <Button data-testid="tree-publier-enrichis-btn" onClick={publierEnrichis} disabled={publishing} className="bg-[#005CFF] hover:bg-[#0047CC]">
          <Sparkles size={15} className="mr-2" /> {publishing ? "Publication…" : "Publier les arbres enrichis"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        <div className="space-y-2">
          {trees.map((t) => (
            <div
              key={t.id}
              data-testid={`tree-item-${t.id}`}
              onClick={() => setSelected(t)}
              className={`cursor-pointer rounded-xl border px-4 py-3 bg-white transition-colors ${selected?.id === t.id ? "border-[#005CFF]" : "border-slate-200 hover:border-slate-400"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{t.titre}</div>
                  <div className="text-xs text-slate-500">
                    {domaineLabel(t.domaine)} · v{t.version} ·{" "}
                    <span className={t.statut === "publie" ? "text-emerald-600 font-semibold" : t.statut === "archive" ? "text-slate-400" : "text-amber-600"}>{t.statut}</span>
                  </div>
                </div>
                <button data-testid={`tree-delete-${t.id}`} onClick={(e) => { e.stopPropagation(); remove(t); }} className="text-slate-300 hover:text-red-500">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {selected && (
          <div className="space-y-5" data-testid="tree-editor">
            <Input data-testid="tree-titre-input" value={selected.titre} onChange={(e) => setSelected({ ...selected, titre: e.target.value })} className="h-12 font-semibold" />
            <div className="flex items-center gap-3">
              <span className="bk-label">Nœud racine</span>
              <Input data-testid="tree-root-input" value={selected.root_node_id} onChange={(e) => setSelected({ ...selected, root_node_id: e.target.value })} className="h-9 w-28 font-mono-bk" />
            </div>

            {selected.nodes.map((n, i) => (
              <div key={`node-${i}`} className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid={`tree-node-${n.id}`}>
                <div className="flex gap-2">
                  <Input value={n.id} onChange={(e) => updateNode(i, { id: e.target.value })} className="h-9 w-24 font-mono-bk text-xs" />
                  <Input data-testid={`node-question-${i}`} value={n.question} onChange={(e) => updateNode(i, { question: e.target.value })} className="h-9 flex-1" placeholder="Question" />
                  <button onClick={() => setSelected({ ...selected, nodes: selected.nodes.filter((_, j) => j !== i) })} className="text-slate-300 hover:text-red-500 px-1">
                    <Trash2 size={15} />
                  </button>
                </div>
                <NodeImage node={n} onChange={(url) => updateNode(i, { image_url: url })} />
                <div className="space-y-2 pl-4 border-l-2 border-slate-100">
                  {n.answers.map((a, j) => (
                    <div key={`answer-${i}-${j}`} className="space-y-2 rounded-lg bg-slate-50/60 p-2">
                      <div className="flex flex-wrap gap-2">
                        <Input data-testid={`answer-label-${i}-${j}`} value={a.label} onChange={(e) => updateAnswer(i, j, { label: e.target.value })} className="h-9 flex-1 min-w-[160px]" placeholder="Réponse" />
                        <Input value={a.next_node_id || ""} onChange={(e) => updateAnswer(i, j, { next_node_id: e.target.value || null })} className="h-9 w-28 font-mono-bk text-xs" placeholder="→ nœud" />
                        <Input data-testid={`answer-solution-${i}-${j}`} value={a.solution || ""} onChange={(e) => updateAnswer(i, j, { solution: e.target.value || null })} className="h-9 flex-1 min-w-[200px]" placeholder="Solution suggérée" />
                        <button onClick={() => updateNode(i, { answers: n.answers.filter((_, k) => k !== j) })} className="text-slate-300 hover:text-red-500 px-1">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      {a.solution && (
                        <ImageField
                          testid={`answer-image-${i}-${j}`}
                          url={a.solution_image_url}
                          label="photo de la solution"
                          onChange={(url) => updateAnswer(i, j, { solution_image_url: url })}
                        />
                      )}
                    </div>
                  ))}
                  <Button data-testid={`add-answer-${i}`} variant="ghost" size="sm" onClick={() => updateNode(i, { answers: [...n.answers, { label: "", next_node_id: null, solution: null }] })}>
                    <Plus size={13} className="mr-1" /> Réponse
                  </Button>
                </div>
              </div>
            ))}

            <Button
              data-testid="add-node-btn"
              variant="outline"
              onClick={() =>
                setSelected({
                  ...selected,
                  nodes: [...selected.nodes, { id: `q${selected.nodes.length + 1}`, question: "", answers: [{ label: "", next_node_id: null, solution: null }] }],
                })
              }
            >
              <Plus size={14} className="mr-1" /> Ajouter une question
            </Button>

            <div className="flex gap-3 sticky bottom-0 bg-slate-50 py-4">
              <Button data-testid="tree-save-btn" onClick={() => save()} className="bg-slate-900 hover:bg-slate-700">
                <Save size={15} className="mr-2" /> Enregistrer
              </Button>
              <Button data-testid="tree-publish-btn" onClick={() => save("publie")} className="bg-[#005CFF] hover:bg-[#0047CC]">
                Publier cette version
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
